import { useDispatch } from "react-redux";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { requestFullProductData } from "../../redux/products/actions";
import { IconType } from "../../common/components/icon/IconType";
import classNames from "classnames";
import { Card } from "../../Configurators/Linear/components/Card";
import { Controller, useFormContext } from "react-hook-form";
import { IconClickable } from "../../common/components/icon/IconClickable";
import {
  type ConfiguredQuickEntryItem,
  type QuickEntryForm,
  type QuickEntryItem,
  type QuickEntryItemPC,
} from "../models";
import { useOnMount } from "../../common/hooks/useOnMount";
import { type SrpProductData } from "../../utils/models";
import { fakeDescriptorFromProduct, isProductPMA } from "../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { NewVersionMessage } from "../../common/components/Messages/NewVersionMessage";
import { type ProductData } from "../../models/ProductData";
import { UndoButton } from "../../common/components/Button/UndoButton";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { MEDIA_QUERY_MIN_TABLET } from "~/utils/screenSizes";
import {
  AutosuggestProductInput,
  isSameProduct,
  useFetchProductAutosuggest,
} from "~/QuickEntryPopup/components/AutosuggestProductInput/AutosuggestProductInput";
import { hasProductConfigured } from "~/QuickEntryPopup/helpers";
import CustomNumberFormat from "~/common/components/CustomNumberFormat";
import Parameter from "~/common/components/Parameter";
import { useTranslation } from "react-i18next";
import { InfoProductMessagePMA } from "~/ProductDetails/components/ProductSummary/InfoProductMessagePMA";
import { Alert } from "~/common/m3/Alert/Alert";

interface RowProps extends DivAttributes {
  index: number;
  validateRow?: (row: ConfiguredQuickEntryItem, itemsPopup?: QuickEntryItemPC[]) => ReactNode[];
  item: QuickEntryItem;
  items?: QuickEntryItemPC[];
  onRemove: () => void;

  variant: "pc" | "wishlist" | "b2c-cart";
}

export const QuickEntryTableRow = ({
  index,
  item,
  className,
  onRemove,
  validateRow,
  variant,
  items,
  ...rest
}: RowProps) => {
  const { t } = useTranslation();
  const {
    control,
    setValue,
    register,
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext<QuickEntryForm>();
  const [isUpdatedWithSuccessor, setUpdatedWithSuccessor] = useState(false);
  const [successorProduct, setSuccessorProduct] = useState<ProductData | null>(null);

  const formNamePrefix = `items.${index}` as const;

  function getFieldName<T extends keyof QuickEntryItem>(name: T) {
    return `items.${index}.${name}` as const;
  }

  const getError = (name: keyof QuickEntryItem) => errors.items?.[index]?.[name] ?? undefined;

  const fromSuccessorClick = useRef(false);

  useOnMount(() => {
    register(getFieldName("product"));
    register(getFieldName("productId"));
    register(getFieldName("previousProductId"));
  });

  const { product, productId, previousProductId } = item;

  const productCode = product?.code;
  const dispatch = useDispatch();
  useEffect(() => {
    if (productCode) {
      dispatch(requestFullProductData(productCode, ""));
    }
  }, [productCode]);

  const qtyError = getError("quantity");
  const productError = getError("product");

  const { inputProps, products, selectedProduct } = useFetchProductAutosuggest(productId ?? "");

  const refreshProductError = (product?: SrpProductData) => {
    if (product) {
      clearErrors(formNamePrefix);
    } else {
      setError(getFieldName("product"), { type: "required" });
    }
  };

  useEffect(() => {
    let productToUpdate: SrpProductData = selectedProduct as SrpProductData;
    if (
      selectedProduct &&
      isProductPMA(fakeDescriptorFromProduct(selectedProduct)) &&
      selectedProduct.successorProduct
    ) {
      productToUpdate = selectedProduct.successorProduct as SrpProductData;
      setUpdatedWithSuccessor(true);
      setSuccessorProduct(productToUpdate);
      setValue(getFieldName("productId"), productToUpdate!.designation);
    } else if (!successorProduct || successorProduct.designation !== selectedProduct?.designation) {
      setUpdatedWithSuccessor(false);
      setSuccessorProduct(null);
    }

    if (fromSuccessorClick.current) {
      fromSuccessorClick.current = false;
    } else {
      setValue(getFieldName("previousProductId"), "");
    }

    setValue(getFieldName("product"), productToUpdate);

    if (productError) {
      refreshProductError(productToUpdate);
    }
  }, [selectedProduct?.code]);

  const isMinTablet = useMediaQuery(MEDIA_QUERY_MIN_TABLET);
  const isPhone = !isMinTablet;

  let validationResult: ReactNode[] = [];
  if (hasProductConfigured(item) && validateRow) {
    validationResult = validateRow(item, items);
  }

  const quantityVisible = () => {
    if (!product) {
      return true;
    }
    return product ? product.quantityVisible : false;
  };

  return (
    <div className={classNames("scha__quick-entry__table__row", className)} {...rest} data-test-id={"quick-entry-row"}>
      {!isPhone && (
        <div className="scha__quick-entry__table__left-side">
          <div className={"px-3 mt-3"}>{index + 1}</div>
        </div>
      )}
      <div className={classNames("scha__quick-entry__table__right-side", isPhone && "w-100")}>
        <Card disableHover={true} className={classNames("py-3", !isMinTablet && "px-2")}>
          <div
            className={classNames(
              "px-0 ",
              isMinTablet && "scha__quick-entry__table__right-side__col-container",
              !isMinTablet && "scha__quick-entry__table__mobile-row",
            )}
          >
            <div>
              <AutosuggestProductInput
                {...inputProps}
                label={t("bulkUpload.table.header.productId")}
                inputValue={productId ?? ""}
                onInputChange={(event, value, reason) => {
                  if (reason !== "reset") {
                    setValue(getFieldName("productId"), value);
                  }
                }}
                onChange={(event, value) => {
                  if (!isSameProduct(value ?? "", selectedProduct)) {
                    setValue(getFieldName("productId"), value ?? "");
                  }
                  refreshProductError(selectedProduct);
                }}
                onBlur={() => {
                  refreshProductError(selectedProduct);
                }}
                products={products}
                disableNewVerLink={true}
                className={classNames(isPhone && "w-100")}
                helperText={" "}
              />
            </div>
            {["wishlist", "b2c-cart"].includes(variant) && (
              <div>
                <Controller
                  render={({ field: { value, onChange, onBlur } }) => (
                    <>
                      <CustomNumberFormat
                        hidden={!quantityVisible()}
                        data-test-id={"quick-entry-item-quantity"}
                        label={t("cartPage.table.header.quantity.title")}
                        handleState={(state) => onChange(state?.floatValue ?? null)}
                        error={!!qtyError}
                        value={value as number | null}
                        onBlur={onBlur}
                      />
                      {!quantityVisible() && <Parameter label={t("input.quantity")} value={item.quantity} />}
                    </>
                  )}
                  name={getFieldName("quantity")}
                  control={control}
                  defaultValue={item.quantity}
                  // @ts-ignore
                  rules={VALIDATE_QTY}
                />
              </div>
            )}

            <div
              className={classNames(
                "scha__quick-entry__table__mobile-row__delete d-flex flex-justify-end",
                !isPhone && "flex-1",
              )}
            >
              <IconClickable
                data-test-id={"quick-entry-item-btn-remove"}
                className={"ml-3"}
                type={IconType.Bin}
                onIconClick={onRemove}
              />
            </div>
          </div>

          <div className="scha__quick-entry__row-info-messages">
            {productError && <Alert severity={"error"}>{t("bulkUpload.error.product.id.invalid")}</Alert>}
            {qtyError && (
              <Alert className="quantity" severity={"error"}>
                {t("bulkUpload.error.product.quantity.invalid")}
              </Alert>
            )}
            {validationResult.map((node) => node)}
            {product && product.isPMA && <InfoProductMessagePMA />}
            {product && product.successorProduct && (
              <NewVersionMessage
                baseProduct={product}
                disableLink={true}
                onClick={() => {
                  fromSuccessorClick.current = true;
                  setValue(getFieldName("previousProductId"), productId);
                  setValue(getFieldName("productId"), product.successorProduct!.designation);
                }}
              />
            )}
            {isUpdatedWithSuccessor && <Alert severity={"info"}>{t("quickEntry.updatedWithSuccessor")}</Alert>}
            {previousProductId && (
              <UndoButton
                onClick={() => {
                  setValue(getFieldName("previousProductId"), "");
                  setValue(getFieldName("productId"), previousProductId);
                }}
              />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

const VALIDATE_QTY = {
  validate: (value: number) => !!value && value >= 1,
};
